import { Injectable } from '@angular/core';
import {
  EmployeeStandardCellView,
  EmployeeStandardUpdateRequest
} from '../../../../../../models/employee-standard-overrides.model';

const EMPLOYEE_STANDARD_REQUEST_KEY_SEPARATOR = '::';
const EMPLOYEE_STANDARD_REQUEST_ASSIGN = 'ASSIGN';
const EMPLOYEE_STANDARD_REQUEST_REMOVE = 'REMOVE';

interface EmployeeStandardUpdateRequestMap {
  [requestKey: string]: EmployeeStandardUpdateRequest;
}

interface EmployeeStandardUpdateStandardBucket {
  standardId: string;
  userIds: string[];
  assigned: boolean;
}

interface EmployeeStandardUpdateUserBucket {
  userId: string;
  standardIds: string[];
  assigned: boolean;
}

interface EmployeeStandardUpdateStandardBucketMap {
  [requestKey: string]: EmployeeStandardUpdateStandardBucket;
}

interface EmployeeStandardUpdateUserBucketMap {
  [requestKey: string]: EmployeeStandardUpdateUserBucket;
}

@Injectable()
export class EmployeeStandardOverridesBulkRequestBuilder {
  public create(changes: EmployeeStandardCellView[]): EmployeeStandardUpdateRequest[] {
    var standardRequests = this.createStandardGroupedPayloads(changes);
    var userRequests = this.createUserGroupedPayloads(changes);
    if (userRequests.length < standardRequests.length) {
      return userRequests;
    }
    return standardRequests;
  }

  private createStandardGroupedPayloads(changes: EmployeeStandardCellView[]): EmployeeStandardUpdateRequest[] {
    var bucketMap = this.createStandardBucketMap(changes);
    var requestMap: EmployeeStandardUpdateRequestMap = {};
    Object.keys(bucketMap).forEach((key) => {
      this.addStandardBucketToRequestMap(requestMap, bucketMap[key]);
    });
    return this.createUpdateRequestList(requestMap);
  }

  private createUserGroupedPayloads(changes: EmployeeStandardCellView[]): EmployeeStandardUpdateRequest[] {
    var bucketMap = this.createUserBucketMap(changes);
    var requestMap: EmployeeStandardUpdateRequestMap = {};
    Object.keys(bucketMap).forEach((key) => {
      this.addUserBucketToRequestMap(requestMap, bucketMap[key]);
    });
    return this.createUpdateRequestList(requestMap);
  }

  private createStandardBucketMap(changes: EmployeeStandardCellView[]): EmployeeStandardUpdateStandardBucketMap {
    var bucketMap: EmployeeStandardUpdateStandardBucketMap = {};
    changes.forEach((cell) => this.addCellToStandardBucketMap(bucketMap, cell));
    return bucketMap;
  }

  private createUserBucketMap(changes: EmployeeStandardCellView[]): EmployeeStandardUpdateUserBucketMap {
    var bucketMap: EmployeeStandardUpdateUserBucketMap = {};
    changes.forEach((cell) => this.addCellToUserBucketMap(bucketMap, cell));
    return bucketMap;
  }

  private addCellToStandardBucketMap(
    bucketMap: EmployeeStandardUpdateStandardBucketMap,
    cell: EmployeeStandardCellView
  ): void {
    if (cell.isDisabled) {
      return;
    }
    this.ensureStandardBucket(bucketMap, cell);
    this.addUniqueId(bucketMap[this.createStandardBucketKey(cell)].userIds, cell.userId);
  }

  private addCellToUserBucketMap(
    bucketMap: EmployeeStandardUpdateUserBucketMap,
    cell: EmployeeStandardCellView
  ): void {
    if (cell.isDisabled) {
      return;
    }
    this.ensureUserBucket(bucketMap, cell);
    this.addUniqueId(bucketMap[this.createUserBucketKey(cell)].standardIds, cell.standardId);
  }

  private ensureStandardBucket(
    bucketMap: EmployeeStandardUpdateStandardBucketMap,
    cell: EmployeeStandardCellView
  ): void {
    var key = this.createStandardBucketKey(cell);
    if (bucketMap[key]) {
      return;
    }
    bucketMap[key] = this.createStandardBucket(cell);
  }

  private ensureUserBucket(
    bucketMap: EmployeeStandardUpdateUserBucketMap,
    cell: EmployeeStandardCellView
  ): void {
    var key = this.createUserBucketKey(cell);
    if (bucketMap[key]) {
      return;
    }
    bucketMap[key] = this.createUserBucket(cell);
  }

  private createStandardBucket(cell: EmployeeStandardCellView): EmployeeStandardUpdateStandardBucket {
    return {
      standardId: cell.standardId,
      userIds: [],
      assigned: cell.isChecked
    };
  }

  private createUserBucket(cell: EmployeeStandardCellView): EmployeeStandardUpdateUserBucket {
    return {
      userId: cell.userId,
      standardIds: [],
      assigned: cell.isChecked
    };
  }

  private addStandardBucketToRequestMap(
    requestMap: EmployeeStandardUpdateRequestMap,
    bucket: EmployeeStandardUpdateStandardBucket
  ): void {
    var key = this.createStandardRequestKey(bucket);
    this.ensureStandardRequest(requestMap, key, bucket);
    this.addUniqueId(requestMap[key].standardIds, bucket.standardId);
  }

  private addUserBucketToRequestMap(
    requestMap: EmployeeStandardUpdateRequestMap,
    bucket: EmployeeStandardUpdateUserBucket
  ): void {
    var key = this.createUserRequestKey(bucket);
    this.ensureUserRequest(requestMap, key, bucket);
    this.addUniqueId(requestMap[key].userIds, bucket.userId);
  }

  private ensureStandardRequest(
    requestMap: EmployeeStandardUpdateRequestMap,
    key: string,
    bucket: EmployeeStandardUpdateStandardBucket
  ): void {
    if (requestMap[key]) {
      return;
    }
    requestMap[key] = this.createStandardUpdateRequest(bucket);
  }

  private ensureUserRequest(
    requestMap: EmployeeStandardUpdateRequestMap,
    key: string,
    bucket: EmployeeStandardUpdateUserBucket
  ): void {
    if (requestMap[key]) {
      return;
    }
    requestMap[key] = this.createUserUpdateRequest(bucket);
  }

  private createStandardUpdateRequest(
    bucket: EmployeeStandardUpdateStandardBucket
  ): EmployeeStandardUpdateRequest {
    return {
      standardIds: [],
      userIds: this.createSortedIds(bucket.userIds),
      assigned: bucket.assigned
    };
  }

  private createUserUpdateRequest(bucket: EmployeeStandardUpdateUserBucket): EmployeeStandardUpdateRequest {
    return {
      standardIds: this.createSortedIds(bucket.standardIds),
      userIds: [],
      assigned: bucket.assigned
    };
  }

  private createUpdateRequestList(requestMap: EmployeeStandardUpdateRequestMap): EmployeeStandardUpdateRequest[] {
    return Object.keys(requestMap).map((key) => this.sortUpdateRequest(requestMap[key]));
  }

  private sortUpdateRequest(request: EmployeeStandardUpdateRequest): EmployeeStandardUpdateRequest {
    return {
      standardIds: this.createSortedIds(request.standardIds),
      userIds: this.createSortedIds(request.userIds),
      assigned: request.assigned
    };
  }

  private createStandardBucketKey(cell: EmployeeStandardCellView): string {
    return this.resolveCellAction(cell) +
      EMPLOYEE_STANDARD_REQUEST_KEY_SEPARATOR +
      cell.standardId;
  }

  private createUserBucketKey(cell: EmployeeStandardCellView): string {
    return this.resolveCellAction(cell) +
      EMPLOYEE_STANDARD_REQUEST_KEY_SEPARATOR +
      cell.userId;
  }

  private createStandardRequestKey(bucket: EmployeeStandardUpdateStandardBucket): string {
    return this.resolveStandardBucketAction(bucket) +
      EMPLOYEE_STANDARD_REQUEST_KEY_SEPARATOR +
      this.createSortedIdKey(bucket.userIds);
  }

  private createUserRequestKey(bucket: EmployeeStandardUpdateUserBucket): string {
    return this.resolveUserBucketAction(bucket) +
      EMPLOYEE_STANDARD_REQUEST_KEY_SEPARATOR +
      this.createSortedIdKey(bucket.standardIds);
  }

  private resolveStandardBucketAction(bucket: EmployeeStandardUpdateStandardBucket): string {
    if (bucket.assigned) {
      return EMPLOYEE_STANDARD_REQUEST_ASSIGN;
    }
    return EMPLOYEE_STANDARD_REQUEST_REMOVE;
  }

  private resolveUserBucketAction(bucket: EmployeeStandardUpdateUserBucket): string {
    if (bucket.assigned) {
      return EMPLOYEE_STANDARD_REQUEST_ASSIGN;
    }
    return EMPLOYEE_STANDARD_REQUEST_REMOVE;
  }

  private resolveCellAction(cell: EmployeeStandardCellView): string {
    if (cell.isChecked) {
      return EMPLOYEE_STANDARD_REQUEST_ASSIGN;
    }
    return EMPLOYEE_STANDARD_REQUEST_REMOVE;
  }

  private createSortedIdKey(ids: string[]): string {
    return this.createSortedIds(ids).join(EMPLOYEE_STANDARD_REQUEST_KEY_SEPARATOR);
  }

  private createSortedIds(ids: string[]): string[] {
    if (!ids) {
      return [];
    }
    return ids.slice().sort();
  }

  private addUniqueId(ids: string[], id: string): void {
    if (ids.indexOf(id) !== -1) {
      return;
    }
    ids.push(id);
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/views/view-group/popups/employee-standard-overrides/services/employee-standard-overrides-bulk-request.builder.ts